import localforage from 'localforage';
import { ref, computed } from 'vue';
import { defineStore } from 'pinia';

import type { SimpleProduct } from '../utils/types';
import { calculationService } from '../utils/api/calculationService';

export const useProductsStore = defineStore(
  'products',
  () => {
    const data = ref<SimpleProduct[] | null>(null);

    const productsById = computed<Record<number, SimpleProduct>>(() => {
      const products: Record<number, SimpleProduct> = {};
      data.value?.forEach((product) => {
        products[product.id] = product;
      });
      return products;
    });

    const productsByCategory = computed<Record<number, SimpleProduct[]>>(() => {
      const products: Record<number, SimpleProduct[]> = {};
      data.value
        ?.filter((product) => product.category_id !== null && product.category_id !== undefined)
        ?.forEach((product) => {
          const categoryId = product.category_id as number;
          if (!products[categoryId]) {
            products[categoryId] = [];
          }
          products[categoryId].push(product);
        });
      return products;
    });

    async function getProductsFromLocalforage() {
      await localforage.getItem<SimpleProduct[]>('products');
    }

    async function fetch() {
      await getProductsFromLocalforage();

      if (!data.value) {
        data.value = await calculationService.getProducts();
      }
    }

    function invalidate() {
      data.value = null;

      fetch();
    }

    function getProductById(id: number) {
      return productsById.value[id] || null;
    }

    function getProductsByCategoryId(categoryId: number) {
      if (!productsByCategory.value) return [];

      return productsByCategory.value[categoryId] || [];
    }

    function getProductPriceByDate(productId: number, date: string): number | null {
      const product = getProductById(productId);

      if (!product) return null;

      const productPrice = product.product_prices.find((price) => {
        const dateFrom = new Date(price.valid_from).getTime();
        const dateTo = price.valid_to ? new Date(price.valid_to).getTime() : new Date().getTime();

        const targetDate = new Date(date).getTime();

        return targetDate >= dateFrom && targetDate <= dateTo;
      });

      return productPrice?.price_float ?? product.product_price.price_float;
    }

    return {
      data,
      fetch,
      invalidate,
      getProductById,
      getProductsByCategoryId,
      getProductPriceByDate,
    };
  },
  {
    // @ts-ignore
    persist: true,
  }
);
